import React, { useEffect, useRef, useState, useImperativeHandle, forwardRef } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import dayjs from 'dayjs';
import { useTheme } from '../../../context/ThemeContext';
import { API_BASE_URL } from '../../../utils/config';
import { formatMoney } from '../../../utils/currency';
import axios from 'axios';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

const CumulativeProfitChart = forwardRef(({ 
    maptriksId, 
    projection = [], 
    onChartUpdate = null,
    autoSave = true 
}, ref) => {
    const { darkMode } = useTheme();
    const chartRef = useRef(null);
    const [invoiceProjection, setInvoiceProjection] = useState(null);
    const effectiveProjection = invoiceProjection ?? projection;

    useEffect(() => {
        let active = true;
        if (!maptriksId) {
            setInvoiceProjection([]);
            return () => { active = false; };
        }

        setInvoiceProjection(null);
        axios.get(`${API_BASE_URL}/ongoru/fatura-kar-zarar/${maptriksId}`)
            .then((response) => {
                if (active) {
                    setInvoiceProjection(Array.isArray(response.data?.data) ? response.data.data : []);
                }
            })
            .catch((error) => {
                console.error('Fatura kar/zarar grafiği yükleme hatası:', error);
                if (active) setInvoiceProjection([]);
            });

        return () => { active = false; };
    }, [maptriksId]);
    const formatCurrency = (amount) => formatMoney(amount);

    const formatDate = (date) => {
        const d = dayjs(date);
        return d.format('MMMM YYYY');
    };

    const saveChartToDB = async (chartData) => {
        if (!autoSave || !maptriksId || !chartData) return;

        try {
            const payload = {
                maptriksID: parseInt(maptriksId),
                chart_type: 'birikimli_kar_zarar',
                slice_config: chartData
            };

            await axios.post(
                `${API_BASE_URL}/ongoru/grafik/kaydet`,
                payload,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        ...(localStorage.getItem('auth_token') && {
                            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
                        })
                    }
                }
            );
        } catch (error) {
            console.error('Grafik kaydetme hatası:', error);
        }
    };

    useImperativeHandle(ref, () => ({
        saveChart: () => {
            if (effectiveProjection.length > 0) {
                const chartData = {
                    labels: effectiveProjection.map(item => formatDate(item.date)),
                    data: effectiveProjection.map(item => item.cumulativeProfit),
                    projection: effectiveProjection
                };
                saveChartToDB(chartData);
            }
        }
    }));

    useEffect(() => {
        if (effectiveProjection.length > 0 && autoSave) {
            const chartData = {
                labels: effectiveProjection.map(item => formatDate(item.date)),
                data: effectiveProjection.map(item => item.cumulativeProfit),
                projection: effectiveProjection
            };
            saveChartToDB(chartData);
            
            if (onChartUpdate) {
                onChartUpdate(chartData);
            }
        }
    }, [effectiveProjection, maptriksId, autoSave]);

    if (!effectiveProjection || effectiveProjection.length === 0) {
        return (
            <div style={{ 
                height: '100%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                color: darkMode ? '#e0e0e0' : '#999'
            }}>
                Grafik verisi bulunamadı
            </div>
        );
    }

    const filteredProjection = effectiveProjection.filter(item => {
        return dayjs(item.date).isAfter(dayjs('2023-12-31')) || dayjs(item.date).isSame(dayjs('2023-12-31'));
    });

    const chartData = {
        labels: filteredProjection.map(item => formatDate(item.date)),
        datasets: [
            {
                label: 'Birikimli Kar/Zarar',
                data: filteredProjection.map(item => item.cumulativeProfit),
                borderColor: darkMode ? '#ffffff' : '#000000',
                borderWidth: 2,
                pointRadius: 1,
                pointHoverRadius: 5,
                tension: 0.2,
                fill: {
                    target: 'origin',
                    above: 'rgba(28, 200, 138, 0.2)',
                    below: 'rgba(231, 74, 59, 0.2)'
                }
            }
        ]
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    callback: value => formatCurrency(value),
                    color: darkMode ? '#e0e0e0' : '#333'
                },
                grid: {
                    color: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'
                }
            },
            x: {
                ticks: {
                    maxRotation: 45,
                    minRotation: 45,
                    color: darkMode ? '#e0e0e0' : '#333'
                },
                grid: {
                    color: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'
                }
            }
        },
        plugins: {
            tooltip: {
                callbacks: {
                    label: function (context) {
                        let label = context.dataset.label || '';
                        if (label) {
                            label += ': ';
                        }
                        if (context.parsed.y !== null) {
                            label += formatCurrency(context.parsed.y);
                        }
                        return label;
                    }
                }
            },
            legend: {
                labels: {
                    color: darkMode ? '#e0e0e0' : '#333'
                }
            },
            title: {
                display: false
            }
        },
        animation: {
            duration: 1000
        }
    };

    return (
        <Line 
            ref={chartRef}
            data={chartData} 
            options={chartOptions} 
        />
    );
});

CumulativeProfitChart.displayName = 'CumulativeProfitChart';

export default CumulativeProfitChart;